import { useQuery } from '@tanstack/react-query';
import { Alert, Flex, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { formatDate, orDash } from '../../lib/format';
import { formatMoney, isPositive } from '../../lib/money';
import { CUSTOMER_HISTORY, fetchCustomerCredit, type CustomerCounterOrder, type CustomerCredit, type CustomerInvoice } from './api';
import { HistoryCard } from './HistoryCard';
import { HistorySection } from './HistorySection';

function hasCredit(c: CustomerCredit): boolean {
  return isPositive(c.credit.held) || isPositive(c.credit.forfeited) || isPositive(c.credit.refunded);
}

/** The customer's invoices (what they paid and still owe) and their counter sales. */
export function InvoicesTab({ customerUid }: { customerUid: string }): ReactNode {
  const { t } = useTranslation();
  const invoiceStatus = (s: string): string => t(`till.status.${s}`, { defaultValue: s });
  const counterStatus = (s: string): string => t(`counter.status.${s}`, { defaultValue: s });
  /** The job card it was billed from, or what kind of sale it was. */
  const source = (row: CustomerInvoice): string =>
    row.jobNo ?? t(`customers.history.invoiceSource.${row.source}`, { defaultValue: row.source });

  const credit = useQuery({
    queryKey: ['customer-credit', customerUid],
    queryFn: () => fetchCustomerCredit(customerUid),
    retry: false,
  });
  const creditDenied = credit.error instanceof ApiError && credit.error.status === 403;

  return (
    <Flex vertical gap={24}>
      {credit.data !== undefined && hasCredit(credit.data) && (
        <Alert
          type="info"
          showIcon
          message={t('customers.history.creditRefundable', { amount: formatMoney(credit.data.credit.refundable, credit.data.currency) })}
          description={
            <Flex vertical gap={2}>
              <Typography.Text className="tabular">
                {[
                  isPositive(credit.data.credit.held) && t('refunds.credit.held', { amount: formatMoney(credit.data.credit.held, credit.data.currency) }),
                  isPositive(credit.data.credit.forfeited) && t('refunds.credit.forfeited', { amount: formatMoney(credit.data.credit.forfeited, credit.data.currency) }),
                  isPositive(credit.data.credit.refunded) && t('refunds.credit.refunded', { amount: formatMoney(credit.data.credit.refunded, credit.data.currency) }),
                ]
                  .filter((part): part is string => part !== false)
                  .join(' · ')}
              </Typography.Text>
              {isPositive(credit.data.credit.refundable) && (
                <Typography.Text>{t('customers.history.creditHeldHelp')}</Typography.Text>
              )}
            </Flex>
          }
        />
      )}
      {creditDenied && <Typography.Text type="secondary">{t('customers.history.creditForbidden')}</Typography.Text>}
      <HistorySection<CustomerInvoice>
        list="invoices"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY.invoices(customerUid, cursor)}
        title={t('customers.history.invoices.title')}
        empty={t('customers.history.invoices.empty')}
        forbidden={t('customers.history.invoices.forbidden')}
        href={(row) => `/pos/invoices/${row.uid}`}
        columns={[
          { title: t('customers.history.col.invoiceNo'), dataIndex: 'invoiceNo', width: 180 },
          { title: t('customers.history.col.date'), dataIndex: 'issuedAt', width: 130, render: (v: string | null) => formatDate(v) },
          { title: t('customers.history.col.source'), key: 'source', render: (_: unknown, row) => source(row) },
          {
            title: t('customers.history.col.total'),
            key: 'total',
            align: 'right',
            render: (_: unknown, row) => <span className="tabular">{formatMoney(row.grandTotal, row.currency)}</span>,
          },
          {
            title: t('customers.history.col.paid'),
            key: 'paid',
            align: 'right',
            render: (_: unknown, row) => <span className="tabular">{formatMoney(row.paidTotal, row.currency)}</span>,
          },
          {
            title: t('customers.history.col.balance'),
            key: 'balance',
            align: 'right',
            render: (_: unknown, row) => <span className="tabular">{formatMoney(row.balanceDue, row.currency)}</span>,
          },
          {
            title: t('customers.history.col.status'),
            dataIndex: 'status',
            render: (v: string) => <Tag>{invoiceStatus(v)}</Tag>,
          },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={row.invoiceNo}
            status={invoiceStatus(row.status)}
            lines={[
              `${source(row)} · ${formatDate(row.issuedAt)}`,
              t('customers.history.totalAndBalance', {
                total: formatMoney(row.grandTotal, row.currency),
                balance: formatMoney(row.balanceDue, row.currency),
              }),
            ]}
          />
        )}
      />

      <HistorySection<CustomerCounterOrder>
        list="counter-orders"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY['counter-orders'](customerUid, cursor)}
        title={t('customers.history.counterOrders.title')}
        empty={t('customers.history.counterOrders.empty')}
        forbidden={t('customers.history.counterOrders.forbidden')}
        // A counter sale has no page of its own; once billed, its invoice is.
        href={(row) => (row.invoiceUid === null ? null : `/pos/invoices/${row.invoiceUid}`)}
        columns={[
          { title: t('customers.history.col.orderNo'), dataIndex: 'orderNo', width: 180 },
          {
            title: t('customers.history.col.status'),
            dataIndex: 'status',
            render: (v: string) => <Tag>{counterStatus(v)}</Tag>,
          },
          {
            title: t('customers.history.col.total'),
            dataIndex: 'grandTotal',
            align: 'right',
            render: (v: string) => <span className="tabular">{formatMoney(v)}</span>,
          },
          { title: t('customers.history.col.invoiceNo'), dataIndex: 'invoiceNo', render: (v: string | null) => orDash(v) },
          { title: t('customers.history.col.created'), dataIndex: 'createdAt', width: 130, render: (v: string) => formatDate(v) },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={row.orderNo}
            status={counterStatus(row.status)}
            lines={[formatMoney(row.grandTotal), row.invoiceNo]}
          />
        )}
      />
    </Flex>
  );
}
